import React from 'react';
import { FileText, Star, BarChart3, Maximize2 } from 'lucide-react';
import InfoRow from './InfoRow';
import { htmlToPlainText } from '../../utils/htmlToPlainText';
import TimedPill from './TimedPill';
import './ExternalListingFront.css';

/**
 * ExternalListingBack - Shows full description and match metadata for external listings
 */
const ExternalListingBack = ({ listing = {}, onOpenDetails = null }) => {
  // Full description - always available per audit.
  // External listings come from ~30 scraped sources and most of them serve this
  // field as HTML (paragraphs, <ul><li> bullets, `&amp;`/`&#39;` entities), so
  // it goes through htmlToPlainText before rendering. What comes back is TEXT
  // and is interpolated as text: never dangerouslySetInnerHTML, because the
  // markup is untrusted third-party input and rendering it would make this
  // provider-facing card a stored-XSS surface. The paragraph and bullet
  // structure survives as newlines, which the `white-space: pre-wrap` on
  // .external-description-full-text turns back into real line breaks.
  const fullDescription =
    htmlToPlainText(listing.fullDescription || listing.description || listing.briefDescription) ||
    'No description available.';
  
  // Priority level
  const priorityLevel = listing.priorityLevel || 'General';
  const isHighPriority = priorityLevel === 'HighPriority' || priorityLevel === 'Priority';
  const priorityDisplay = priorityLevel === 'HighPriority' ? 'High Priority' : 
                         priorityLevel === 'Priority' ? 'Priority' : null;
  
  // Match metadata (if available from swipe deck)
  const matchScore = listing._matchScore;

  return (
    <div className="card-face-content listing-gradient-bg external-listing-back" role="region" aria-label="External Listing Back">
      {/* Full Description Header */}
      <div className="external-back-header">
        <FileText size={18} className="external-back-icon" />
        <h3 className="external-back-title">Full Description</h3>
      </div>
      
      {/* Scrollable Description Area */}
      <div className="external-description-full">
        {onOpenDetails ? (
          <button
            type="button"
            className="listing-description-trigger listing-description-trigger--external"
            onClick={(event) => {
              event.stopPropagation();
              onOpenDetails();
            }}
            aria-label="Open the complete listing description and links"
          >
            <p className="external-description-full-text">{fullDescription}</p>
            <span className="listing-description-trigger__cta">
              Full description &amp; links
              <Maximize2 size={14} aria-hidden="true" />
            </span>
          </button>
        ) : (
          <p className="external-description-full-text">{fullDescription}</p>
        )}
      </div>
      
      {/* Match Metadata Section */}
      <div className="external-metadata-section">
        {/* Priority Badge */}
        {isHighPriority && priorityDisplay && (
          <InfoRow Icon={Star}>
            <TimedPill
              id="priority-level"
              label={priorityDisplay}
              tooltipText="This listing has been marked as high priority by the poster"
              className="priority-pill"
            />
          </InfoRow>
        )}
        
        {/* Match Score */}
        {matchScore !== undefined && matchScore !== null && (
          <InfoRow Icon={BarChart3}>
            <TimedPill
              id="match-score"
              label={`${Math.round(matchScore)}% Match`}
              tooltipText="How well this listing matches your preferences"
              className="match-score-pill"
            />
          </InfoRow>
        )}
      </div>
    </div>
  );
};

export default ExternalListingBack;
